import React, { useEffect, useState } from 'react';
import _ from 'lodash';

import { NewAnomalyItem, FVIStructureItem, PDFImageProps } from '../../../../utils/models';
import { matchPhrases } from '../../../../utils/utilities';
import { getStructureAnomalyClassOptions } from '../../../../utils/data';

import AccordionItem from '../accordion-item';
import Conditional from '../../../common/conditional';
import Confirm from '../../../themed/confirm';
import CustomAnomalyItems from '../../custom-general/custom-anomaly-items';
import StructuralMemberForm from '../../../forms/custom-fvi/structuralMemberForm';
import VIGeneralDrawingForm from '../../../forms/visual-inspection/viGeneralDrawingForm';
import VIPhotoRecordForm from '../../../forms/visual-inspection/viPhotoRecordForm';

import classNames from './style.module.scss';

interface ExistingStructureItemProps {
	nextAnomalyID?: number;
	structureItem: FVIStructureItem;
	isOpenOnDefault?: boolean;
	isPDOFormat: boolean;
	onDeleteStructureItem: (structureMemberName: string) => void;
	onChangeStructureAnomaly: (structureMemberName: string, items: NewAnomalyItem[]) => void;
	onChangeGeneralPhotos: (structureMemberName: string, images: PDFImageProps[]) => void;
	onChangeGeneralDrawing: (structureMemberName: string, images: PDFImageProps[]) => void;
	onEditStructureItem: (initialItem: FVIStructureItem, updatedItem: Pick<FVIStructureItem,  'structureMemberName' | 'hasAnomalies' | 'inspectionDescription'>) => void;
}

const ExistingStructureItem: React.FC<ExistingStructureItemProps> = ({
	structureItem,
	nextAnomalyID,
	isOpenOnDefault,
	isPDOFormat,
	onDeleteStructureItem,
	onEditStructureItem,
	onChangeStructureAnomaly,
	onChangeGeneralPhotos,
	onChangeGeneralDrawing,
}) => {
	const [deleteStructureItem, setDeleteStructureItem] = useState(false);
	const [editStructureItem, setEditStructureItem] = useState(false);
	const [isOpen, setOpen] = useState(false);

	const handleEditInspectedItem = (newStructureDetails: Pick<FVIStructureItem, 'structureMemberName' | 'hasAnomalies' | 'inspectionDescription'>) => {
		onEditStructureItem(structureItem, newStructureDetails);
		setEditStructureItem(false);
		setOpen(true);
	};

	const handleToggleEdit = () => {
		setEditStructureItem((previous) => !previous);
	};

	const handleConfirmDeletion = () => {
		onDeleteStructureItem(structureItem.structureMemberName);
	};

	const handleToggleDeletion = () => {
		setDeleteStructureItem((previous) => !previous);
	};

	useEffect(() => {
		if (isOpenOnDefault) setOpen(isOpenOnDefault);
	}, [isOpenOnDefault]);

	return (
		<>
			<Conditional isVisible={deleteStructureItem}>
				<Confirm
					title={'Delete Structural Member ' + structureItem.structureMemberName + '?'}
					message="Are you sure you want to delete this item? This action is not reversible!"
					onCancel={handleToggleDeletion}
					onConfirm={handleConfirmDeletion}
				/>
			</Conditional>

			<Conditional isVisible={editStructureItem}>
				<div className="bg-white round border p-3">
					<StructuralMemberForm
						structureDetails={_.pick(structureItem, ['structureMemberName', 'hasAnomalies', 'inspectionDescription'])} 
						onSave={handleEditInspectedItem}
						onCancelEdit={handleToggleEdit}
					/>
				</div>
			</Conditional>

			<Conditional isVisible={!deleteStructureItem && !editStructureItem}>
				<div className={classNames.accordionItem}>
					<div className={classNames.accordionItemHeader} onClick={() => setOpen((prev) => !prev)} role="button">
						<div>
							Structure Member Name: {structureItem.structureMemberName}
						</div>

						<div className="d-flex justify-content-center align-items-center">
							<button className={classNames.accordionItemEdit} onClick={handleToggleEdit}>
								<i className="bi bi-pencil"></i>
							</button>

							<button className={classNames.accordionItemDelete} onClick={handleToggleDeletion}>
								<i className="bi bi-trash"></i>
							</button>

							<div className={classNames.accordionItemIcon}>
								<i className={`bi bi-chevron-${isOpen ? 'down' : 'right'}`}></i>
							</div>
						</div>
					</div>

					<Conditional isVisible={isOpen}>
						<div className={classNames.accordionItemBody}>
							<Conditional isVisible={matchPhrases(structureItem.hasAnomalies, 'yes')}>
								<AccordionItem isActive title="Anomaly Items">
									<CustomAnomalyItems
										isPDOFormat={isPDOFormat}
										nextAnomalyID={nextAnomalyID ?? structureItem.anomalyItems.length + 1}
										locationOptions={[]}
										isNewFormat={false}
										damageClasses={getStructureAnomalyClassOptions()}
										items={structureItem.anomalyItems}
										onDataChange={(items) => onChangeStructureAnomaly(structureItem.structureMemberName, items)}
									/>
								</AccordionItem>
							</Conditional>

							<Conditional isVisible={matchPhrases(structureItem.hasAnomalies, 'no')}>
								<AccordionItem isActive title="General Drawing">
									<VIGeneralDrawingForm 
										initialGeneralDrawing={structureItem.generalDrawing}
										onProceed={(images: PDFImageProps[]) => onChangeGeneralDrawing(structureItem.structureMemberName, images)} 
									/>
								</AccordionItem>
								
								<AccordionItem isActive title="General Photos">
									<VIPhotoRecordForm 
										initialGeneralPhotos={structureItem.generalPhotos}
										onProceed={(images: PDFImageProps[]) => onChangeGeneralPhotos(structureItem.structureMemberName, images)} 
									/>
								</AccordionItem>
							</Conditional>
						</div>
					</Conditional>
				</div>
			</Conditional>
		</>
	);
};

export default ExistingStructureItem;
